import { isTenantOwnerAdmin } from "utils/isTenantOwnerAdmin"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Filters from "components/Common/Filters"
import { Link, useLocation, useNavigate } from "react-router-dom"
import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Paginations"
import { useSelector } from "react-redux"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import { useDispatch } from "react-redux"
import {
  getAllOrders,
  syncOrderWithPos,
  updateOrderStatus,
} from "store/actions/orderAction"
import TableShimmer from "components/Shared/TableShimmer"
import { getAllBranchs } from "store/actions/usersAction"
import { getAllBranchsList } from "store/actions/branchsActions"
import { post } from "helpers/api_helper"
import { RIDERS_FOR_ASSIGNMENT } from "helpers/url_helper"
import OrdersDeliveryMap from "components/Orders/OrdersDeliveryMap"
import OrderMapDetailModal from "components/Orders/OrderMapDetailModal"
import Swal from "sweetalert2"
import {
  buildOrderExportPayload,
  canExportOrders,
  downloadOrderExport,
} from "utils/orderExportUtils"
import { downloadOrderDetailPdf } from "helpers/orderDetailPdf"
import { hasWalletRelatedPermission } from "utils/walletPermissionUtils"
import {
  commonItemPerPage,
  getPerPageOptions,
  mobileOrderStatus,
  paymentMethodTypes,
} from "components/Common/constants"
import Select from "components/Shared/TranslatedSelect"
import { showAlert } from "store/reducers/alertReducer"
import { useTranslation } from "react-i18next"
import LtrText from "components/Shared/LtrText"
import { getCurrencyLabel } from "utils/currency"
import { formatAppVersionLabel } from "utils/displayLabels"
import { setItemsPerPage } from "store/reducers/orderReducer"
import { getAppVersions } from "store/actions/customersAction"
import AssignToRiderModal from "components/Orders/AssignToRiderModal"
import AssignToBranchModal from "components/Orders/AssignToBranchModal"
import OrderNoCopyCell from "./OrderNoCopyCell"
import OrdersTableScroll from "./OrdersTableScroll"
import {
  buildOrderPosSource,
  canShowManualPosPushButton,
  getTenantIntegrationConnection,
  isFnbPosProvider,
  isFoodicsProvider,
  isOrderPosBranchLinked,
  hasExternalPosIntegration,
  isPushCapablePosBrand,
  resolveOrderPosIntegrationCode,
} from "utils/orderPosUtils"

const POS_PUSH_FAILED_MESSAGE =
  "Unable to send the order to POS. Please try again."
import useTenantPosConnections from "hooks/useTenantPosConnections"
import {
  getSystemOrderStatusLabel,
  getSystemStatusDisplay,
  getSystemStatusFilterOptions,
  getSyrvePosStatusFilterOptions,
  getFoodicsPosStatusFilterOptions,
  getPosStatusLabel,
  getPosStatusColor,
  resolveSystemOrderStatusValue,
} from "utils/orderStatusUtils"
import { Input } from "reactstrap"
import formatDisplayDateTime from "components/Shared/formatDateTime"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import { useAssignedBrands } from "hooks/useAssignedBrands"
import { brandShowsScheduledDeliveryDateInOrders } from "utils/deliveryProfileHelpers"
import { getAllowedOrderTypeFilterOptions } from "utils/fulfillmentPermissions"
import {
  filterOrdersForAppliedMapFilters,
  getBranchCoords,
} from "utils/mapMarkerUtils"
import { getPaymentMethodsScope } from "store/actions/globalSettingAction"
import { filterPaymentMethodTypesByTenantScope } from "pages/AppSettings/paymentMethodConfig"

const formatFilterDateTime = date => {
  const pad = value => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const parseBranchIdParam = param =>
  param
    ? param.split(",").map(item => {
        const [label, value] = item.split(":")
        return { label, value }
      })
    : []

const getDefaultDateRange = () => {
  const today = new Date()
  const startOfDay = new Date(today)
  startOfDay.setHours(0, 0, 0, 0)

  const endOfDay = new Date(today)
  endOfDay.setHours(23, 59, 0, 0)

  return {
    min: formatFilterDateTime(startOfDay),
    max: formatFilterDateTime(endOfDay),
  }
}

const MAP_ORDERS_PER_PAGE = 500

const ORDER_STATUS_SELECT_MENU_PROPS = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  menuPlacement: "auto",
  menuShouldScrollIntoView: false,
}

const orderStatusSelectStyles = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: base => ({ ...base, zIndex: 9999 }),
}

const Orders = ({ brandConfig = null }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const ordersBasePath = brandConfig
    ? `/orders/${brandConfig.brandId}`
    : "/f&b-orders"
  const brandHasPushPos = brandConfig
    ? isPushCapablePosBrand(brandConfig)
    : (assignedBrandsList || []).some(b => isPushCapablePosBrand(b))
  const showPosStatusColumn =
    brandConfig != null
      ? isFnbPosProvider(brandConfig)
      : (assignedBrandsList || []).some(b => isFnbPosProvider(b))
  const showServiceProviderColumn = brandConfig
    ? hasExternalPosIntegration(brandConfig)
    : (assignedBrandsList || []).some(b => hasExternalPosIntegration(b))
  const serviceProvider = brandHasPushPos
    ? brandConfig?.serviceProvider || "Syrve"
    : ""
  // Orders.brandId is stored as string — keep filter payload as string
  const brandId =
    brandConfig?.brandId != null && String(brandConfig.brandId).trim() !== ""
      ? String(brandConfig.brandId).trim()
      : ""
  const activeOrderStatusMap = mobileOrderStatus
  const isFoodicsBrand = isFoodicsProvider(brandConfig || {})
  const resolveListStatus = status =>
    !brandHasPushPos && Number(status) === 6 ? 0 : Number(status) || 0
  const { hasOnlyOneBrand, brands: assignedBrandsList } = useAssignedBrands()
  const { byCode: tenantPosByCode } = useTenantPosConnections()
  const showScheduledDeliveryDate =
    brandShowsScheduledDeliveryDateInOrders(brandConfig)
  const pageTitle = hasOnlyOneBrand
    ? t("Orders")
    : brandConfig?.displayName ||
      brandConfig?.brandName ||
      t("F&B Orders")

  document.title = `${pageTitle} `

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const branchIdParam = searchParams.get("branchId")
  const orderTypeParam = searchParams.get("orderType")
  const assignmentStatusParam = searchParams.get("assignmentStatus")
  const platformParam = searchParams.get("platform")
  const paymentMethodParam = searchParams.get("paymentMethod")
  const paymentStatusParam = searchParams.get("paymentStatus")
  const minDateParam = searchParams.get("minDate")
  const maxDateParam = searchParams.get("maxDate")
  const posStatusParam = searchParams.get("posStatus") || "0"

  const {
    loading,
    isLoading,
    isUpdating,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    orders,
  } = useSelector(state => state.Orders)
  const { branchsList } = useSelector(state => state.Users)
  const { appVersions } = useSelector(state => state.Customers)
  const { userRole, accountType, accountScope } = useSelector(
    state => state.Login
  )
  const { paymentMethodsScope } = useSelector(state => state.GlobalSettings)
  const branchSubPermissions = getSubPermissions(
    userRole,
    5,
    "branchLocations_3",
    accountType,
    accountScope
  )
  const orderViewSubPermissions = useMemo(
    () => getSubPermissions(userRole, 9, "order_1", accountType) || {},
    [userRole, accountType]
  )
  const orderAssignSubPermissions = useMemo(
    () => getSubPermissions(userRole, 9, "order_3", accountType) || {},
    [userRole, accountType]
  )
  /** Branch filter + /branch/all require Branches → View. */
  const canFilterByBranch = useMemo(
    () =>
      isPermissionEnabled(
        userRole,
        5,
        "branchLocations_1",
        accountType,
        accountScope
      ),
    [userRole, accountType, accountScope]
  )
  /**
   * Rider filter + list-for-assignment require Delivery Partners view
   * or Orders → Assign to rider (or tenant owner admin).
   */
  const canFilterByRider = useMemo(
    () =>
      isTenantOwnerAdmin() ||
      isPermissionEnabled(
        userRole,
        47,
        "delivery_partners_1",
        accountType,
        accountScope
      ) ||
      Boolean(orderAssignSubPermissions?.assign_orders_to_rider?.isEnabled),
    [userRole, accountType, accountScope, orderAssignSubPermissions]
  )
  /**
   * Payment method scope API allows order_1; keep filter for order viewers.
   * Still requires a hydrated role so we do not fire before login context is ready.
   */
  const canFilterByPaymentMethod = useMemo(
    () =>
      isPermissionEnabled(userRole, 9, "order_1", accountType, accountScope) ||
      isPermissionEnabled(
        userRole,
        32,
        "app_settings_1",
        accountType,
        accountScope
      ),
    [userRole, accountType, accountScope]
  )
  const hasMapPermission = Boolean(
    orderViewSubPermissions?.city_zones_map?.isEnabled &&
      isPermissionEnabled(userRole, 9, "order_1", accountType)
  )
  const showMapTab = hasMapPermission
  const viewParam = searchParams.get("view")
  const orderTypeFilterOptions = getAllowedOrderTypeFilterOptions(
    branchSubPermissions
  ).map(option => ({
    ...option,
    label: t(option.label),
  }))

  const paymentMethodScopeCards = useMemo(() => {
    const cards = paymentMethodsScope?.cards || []
    if (!brandId) return cards
    return cards.filter(card => String(card.brandId) === brandId)
  }, [paymentMethodsScope, brandId])

  const allowedPaymentMethodTypes = useMemo(
    () =>
      filterPaymentMethodTypesByTenantScope(
        paymentMethodTypes,
        paymentMethodScopeCards
      ),
    [paymentMethodScopeCards]
  )

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [searchTerm2, setSearchTerm2] = useState("")

  // const [appVersion, setAppVersion] = useState("")

  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: parseInt(searchParams.get("sortOrder") || "2"),
    branchId: parseBranchIdParam(branchIdParam),
    status: parseInt(searchParams.get("status") || "0"),
    posStatus: parseInt(posStatusParam || "0"),
    orderType: orderTypeParam
      ? orderTypeParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    platform: platformParam
      ? platformParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    appVersion: "",
    assignmentStatus: assignmentStatusParam || "",
    paymentMethod: paymentMethodParam
      ? paymentMethodParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    paymentStatus: paymentStatusParam || "",
    riderId: searchParams.get("riderId") || "",
  })

  const [activeView, setActiveView] = useState(
    viewParam === "map" && showMapTab ? "map" : "list"
  )
  const [mapBranches, setMapBranches] = useState([])
  const [mapBranchesLoading, setMapBranchesLoading] = useState(false)
  const [riderFilterOptions, setRiderFilterOptions] = useState([])
  const [ridersFilterLoading, setRidersFilterLoading] = useState(false)
  const [mapSelectedOrder, setMapSelectedOrder] = useState(null)
  const [showMapOrderModal, setShowMapOrderModal] = useState(false)
  const [appliedMapFilters, setAppliedMapFilters] = useState(() => ({
    branchId: parseBranchIdParam(branchIdParam),
    riderId: searchParams.get("riderId") || "",
    status: parseInt(searchParams.get("status") || "0"),
    assignmentStatus: assignmentStatusParam || "",
    estimatedDeliveryDateRange: {},
  }))
  const [appliedMapDateRange, setAppliedMapDateRange] = useState(() =>
    minDateParam && maxDateParam
      ? { min: minDateParam, max: maxDateParam }
      : getDefaultDateRange()
  )

  const [selectedOrderIds, setSelectedOrderIds] = useState([])
  const [showAssignRiderModal, setShowAssignRiderModal] = useState(false)
  const [showAssignBranchModal, setShowAssignBranchModal] = useState(false)

  const listFlatpickrRef = useRef(null)
  const mapFlatpickrRef = useRef(null)
  const estimatedDeliveryFlatpickrRef = useRef(null)
  const mapEstimatedDeliveryFlatpickrRef = useRef(null)

  const [dateRange, setDateRange] = useState(() =>
    minDateParam && maxDateParam
      ? { min: minDateParam, max: maxDateParam }
      : getDefaultDateRange()
  )
  const [estimatedDeliveryDateRange, setEstimatedDeliveryDateRange] =
    useState({})
  const [showModal, setShowModal] = useState(false)
  const [downloadingPdfId, setDownloadingPdfId] = useState(null)

  const mapFilters = useMemo(() => {
    const filters = []
    if (canFilterByBranch) {
      filters.push({
        name: "branchId",
        type: "multiSelect",
        options: branchsList.map(branch => ({
          label: language === "sa" ? branch.nameAr : branch.name,
          value: branch._id,
        })),
        placeholder: t("Select Branch"),
      })
    }
    if (canFilterByRider) {
      filters.push({
        name: "riderId",
        type: "select",
        disabled: ridersFilterLoading,
        options: ridersFilterLoading ? [] : riderFilterOptions,
        placeholder: ridersFilterLoading
          ? t("Loading riders...")
          : t("All Riders"),
      })
    }
    // The map only shows delivery orders, so use the delivery status names.
    filters.push({
      name: "status",
      type: "select",
      options: getSystemStatusFilterOptions(t).map(option => ({
        ...option,
        label: option.value
          ? getSystemOrderStatusLabel(option.value, "delivery", t)
          : option.label,
      })),
      placeholder: t("All"),
      hidePlaceholder: true,
    })
    if (canFilterByRider) {
      filters.push({
        name: "assignmentStatus",
        type: "select",
        options: [
          { label: t("Assigned to Rider"), value: "assigned" },
          { label: t("Not Assigned"), value: "unassigned" },
        ],
        placeholder: t("All Assignments"),
      })
    }
    return filters
  }, [
    canFilterByBranch,
    canFilterByRider,
    branchsList,
    language,
    riderFilterOptions,
    ridersFilterLoading,
    t,
  ])

  const listFilters = useMemo(() => {
    const filters = [
      {
        name: "sortOrder",
        type: "select",
        options: [
          { label: t("Ascending"), value: 1 },
          { label: t("Descending"), value: 2 },
        ],
        placeholder: t("Ascending"),
      },
    ]
    if (canFilterByBranch) {
      filters.push({
        name: "branchId",
        type: "multiSelect",
        options: branchsList.map(branch => ({
          label: language === "sa" ? branch.nameAr : branch.name,
          value: branch._id,
        })),
        placeholder: t("Select Branch"),
      })
    }
    filters.push({
      name: "status",
      type: "select",
      options: [
        ...getSystemStatusFilterOptions(t),
        ...(brandHasPushPos
          ? [{ label: t("Sent To Branch Failed"), value: 6 }]
          : []),
      ],
      placeholder: t("All"),
      hidePlaceholder: true,
    })
    if (showPosStatusColumn) {
      filters.push({
        name: "posStatus",
        type: "select",
        options: isFoodicsBrand
          ? getFoodicsPosStatusFilterOptions(t)
          : getSyrvePosStatusFilterOptions(t),
        placeholder: t("POS Status"),
      })
    }
    filters.push({
      name: "orderType",
      type: "multiSelect",
      options: orderTypeFilterOptions,
      placeholder: t("Select Order Type"),
    })
    if (canFilterByRider) {
      filters.push({
        name: "assignmentStatus",
        type: "select",
        options: [
          { label: t("Assigned to Rider"), value: "assigned" },
          { label: t("Not Assigned"), value: "unassigned" },
        ],
        placeholder: t("All Assignments"),
      })
    }
    filters.push({
      name: "platform",
      type: "multiSelect",
      options: [
        { label: t("Android"), value: "android" },
        { label: t("iOS"), value: "iOS" },
        { label: t("Website"), value: "website" },
      ],
      placeholder: t("Platform"),
    })
    if (canFilterByPaymentMethod) {
      filters.push(
        {
          name: "paymentMethod",
          type: "multiSelect",
          options: allowedPaymentMethodTypes.map(item => ({
            label: t(item.label),
            value: item.value,
          })),
          placeholder: t("Payment Method"),
        },
        {
          name: "paymentStatus",
          type: "select",
          options: [
            { label: t("Pending"), value: 0 },
            { label: t("Paid"), value: 1 },
            { label: t("Refunded"), value: 2 },
          ],
          placeholder: t("All"),
        }
      )
    }
    if (
      activeView === "list" &&
      selectedFilters?.platform?.some(
        item => item.value === "android" || item.value === "iOS"
      )
    ) {
      filters.push({
        name: "appVersion",
        type: "select",
        options: appVersions.map(item => ({
          label: item,
          value: item,
        })),
        placeholder: t("App Versions"),
      })
    }
    return filters
  }, [
    t,
    canFilterByBranch,
    canFilterByRider,
    canFilterByPaymentMethod,
    branchsList,
    language,
    brandHasPushPos,
    showPosStatusColumn,
    isFoodicsBrand,
    orderTypeFilterOptions,
    allowedPaymentMethodTypes,
    activeView,
    selectedFilters?.platform,
    appVersions,
  ])

  const buildOrdersQueryPayload = useCallback(
    ({ page = pageNumber, perPage = itemsPerPage, forMap = false } = {}) => {
      const {
        status,
        posStatus,
        sortOrder,
        branchId,
        orderType,
        platform,
        appVersion,
        assignmentStatus,
        paymentMethod,
        paymentStatus,
        riderId,
      } = selectedFilters
      const branchIds = canFilterByBranch
        ? branchId?.map(item => item.value) || []
        : []
      const updatedOrderType = forMap
        ? ["delivery"]
        : orderType?.map(item => item.value) || []
      const updatedPaymentMethod = canFilterByPaymentMethod
        ? paymentMethod?.map(item => item.value) || []
        : []
      const updatedPlatform =
        platform?.flatMap(item => {
          if (item.value === "android") return [item.value, "Android"]
          if (item.value === "website") return [item.value, "Website"]
          return item.value
        }) || []
      const resolvedAssignmentStatus = canFilterByRider
        ? assignmentStatus || ""
        : ""
      const resolvedRiderId =
        canFilterByRider && forMap && riderId ? [String(riderId)] : undefined

      return {
        perPage: forMap ? MAP_ORDERS_PER_PAGE : perPage,
        pageNo: forMap ? 1 : page,
        orderBy: sortOrder,
        keyword: forMap ? "" : searchTerm,
        orderNo: forMap ? "" : searchTerm2 || "",
        appVersion:
          !forMap &&
          platform?.some(
            item => item.value === "android" || item.value === "iOS"
          )
            ? appVersion
            : "",
        status: resolveListStatus(status),
        posStatus:
          !forMap && posStatus && Number(posStatus) !== 0
            ? Number(posStatus)
            : undefined,
        branchId: branchIds,
        orderType: updatedOrderType,
        assignmentStatus: resolvedAssignmentStatus,
        deliveryPartnerId: resolvedRiderId,
        platform: forMap ? [] : updatedPlatform,
        paymentMethod: forMap ? [] : updatedPaymentMethod,
        paymentStatus:
          !canFilterByPaymentMethod ||
          forMap ||
          paymentStatus === "" ||
          paymentStatus == null
            ? undefined
            : Number(paymentStatus),
        serviceProvider,
        brandId,
        dateRange:
          dateRange.min && dateRange.max
            ? { min: dateRange.min, max: dateRange.max }
            : {},
        estimatedDeliveryDateRange:
          showScheduledDeliveryDate &&
          estimatedDeliveryDateRange.min &&
          estimatedDeliveryDateRange.max
            ? {
                min: estimatedDeliveryDateRange.min,
                max: estimatedDeliveryDateRange.max,
              }
            : {},
      }
    },
    [
      pageNumber,
      itemsPerPage,
      selectedFilters,
      searchTerm,
      searchTerm2,
      dateRange,
      estimatedDeliveryDateRange,
      showScheduledDeliveryDate,
      canFilterByBranch,
      canFilterByRider,
      canFilterByPaymentMethod,
      serviceProvider,
      brandId,
      resolveListStatus,
    ]
  )

  const fetchMapBranches = useCallback(async () => {
    if (!canFilterByBranch) {
      setMapBranches([])
      return
    }
    setMapBranchesLoading(true)
    try {
      const action = await dispatch(
        getAllBranchsList({
          perPage: 500,
          pageNo: 1,
          orderBy: 1,
          status: 2,
          keyword: "",
        })
      )
      setMapBranches(action?.payload?.data || [])
    } catch {
      setMapBranches([])
    } finally {
      setMapBranchesLoading(false)
    }
  }, [dispatch, canFilterByBranch])

  const fetchRiderFilterOptions = useCallback(async (branchIds = []) => {
    if (!canFilterByRider) {
      setRiderFilterOptions([])
      return
    }
    setRidersFilterLoading(true)
    try {
      let riders = []

      if (!branchIds.length) {
        const response = await post(RIDERS_FOR_ASSIGNMENT, {
          branchId: "all",
          availabilityFilter: "all",
        })
        riders = response?.data || []
      } else if (branchIds.length === 1) {
        const response = await post(RIDERS_FOR_ASSIGNMENT, {
          branchId: String(branchIds[0]),
          availabilityFilter: "all",
        })
        riders = response?.data || []
      } else {
        const responses = await Promise.all(
          branchIds.map(branchId =>
            post(RIDERS_FOR_ASSIGNMENT, {
              branchId: String(branchId),
              availabilityFilter: "all",
            })
          )
        )
        const ridersById = new Map()
        responses.forEach(response => {
          ;(response?.data || []).forEach(rider => {
            ridersById.set(String(rider._id), rider)
          })
        })
        riders = Array.from(ridersById.values())
      }

      setRiderFilterOptions(
        riders.map(rider => ({
          label: rider.name,
          value: rider._id,
        }))
      )
    } catch {
      setRiderFilterOptions([])
    } finally {
      setRidersFilterLoading(false)
    }
  }, [canFilterByRider])

  // Initial orders fetch only (filter APIs are gated in separate effects below).
  useEffect(() => {
    fetchAllOrders()
  }, [])

  useEffect(() => {
    if (!canFilterByBranch) return
    fetchAllBranchs()
  }, [canFilterByBranch])

  useEffect(() => {
    if (!canFilterByPaymentMethod) return
    dispatch(getPaymentMethodsScope())
  }, [canFilterByPaymentMethod, dispatch])

  useEffect(() => {
    if (!showMapTab) return
    if (canFilterByBranch) {
      fetchMapBranches()
    }
    if (canFilterByRider) {
      const initialBranchIds = canFilterByBranch
        ? parseBranchIdParam(branchIdParam).map(item => String(item.value)) ||
          []
        : []
      fetchRiderFilterOptions(initialBranchIds)
    }
  }, [
    showMapTab,
    canFilterByBranch,
    canFilterByRider,
    fetchMapBranches,
    fetchRiderFilterOptions,
    branchIdParam,
  ])

  // Clear module-gated filter selections when the user lacks those permissions.
  useEffect(() => {
    setSelectedFilters(prev => {
      let next = prev
      if (!canFilterByBranch && (prev.branchId?.length || 0) > 0) {
        next = { ...next, branchId: [] }
      }
      if (
        !canFilterByRider &&
        (prev.riderId || prev.assignmentStatus)
      ) {
        next = { ...next, riderId: "", assignmentStatus: "" }
      }
      if (
        !canFilterByPaymentMethod &&
        ((prev.paymentMethod?.length || 0) > 0 ||
          (prev.paymentStatus !== "" && prev.paymentStatus != null))
      ) {
        next = { ...next, paymentMethod: [], paymentStatus: "" }
      }
      return next
    })
  }, [canFilterByBranch, canFilterByRider, canFilterByPaymentMethod])

  // Drop previously selected methods that are no longer allowed for this tenant/brand.
  useEffect(() => {
    if (!canFilterByPaymentMethod) return
    const allowedValues = new Set(
      allowedPaymentMethodTypes.map(item => item.value)
    )
    setSelectedFilters(prev => {
      const current = prev.paymentMethod || []
      if (!Array.isArray(current) || current.length === 0) return prev
      const next = current.filter(item => allowedValues.has(item.value))
      if (next.length === current.length) return prev
      return { ...prev, paymentMethod: next }
    })
  }, [allowedPaymentMethodTypes, canFilterByPaymentMethod])

  useEffect(() => {
    if (viewParam === "map") {
      if (showMapTab) {
        setActiveView("map")
      } else {
        setActiveView("list")
        const nextParams = new URLSearchParams(location.search)
        nextParams.delete("view")
        if (!nextParams.get("page")) {
          nextParams.set("page", "1")
        }
        navigate(`${ordersBasePath}?${nextParams.toString()}`, { replace: true })
      }
    } else if (viewParam !== "map") {
      setActiveView("list")
    }
  }, [showMapTab, viewParam, location.search, navigate, ordersBasePath])

  const fetchAllOrders = () => {
    const payload = buildOrdersQueryPayload({
      forMap: activeView === "map",
    })
    dispatch(getAllOrders(payload))
    setIsApiCalled(true)
  }

  useEffect(() => {
    const { platform } = selectedFilters
    if (
      platform?.some(
        item => item.value === "android" || item.value === "iOS"
      )
    ) {
      fetchAppVersions()
    }
  }, [selectedFilters])

  const fetchAppVersions = () => {
    const { platform } = selectedFilters
    const updatedPlatform =
      platform
        ?.filter(item => item.value === "android" || item.value === "iOS")
        .map(item => item.value) || []
    const transformedPlatform = updatedPlatform?.map(item =>
      item === "android" ? "Android" : item
    )
    const payload = { platform: transformedPlatform }
    dispatch(getAppVersions(payload))
  }

  const fetchAllBranchs = () => {
    dispatch(getAllBranchs())
  }

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
    assignToRider: null,
    assignToBranch: null,
    export: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    // Wait until role is hydrated — empty/array placeholders must not force redirect.
    if (!userRole || Array.isArray(userRole) || !userRole.permissions) {
      return
    }

    const isPermissionEdit = getSubPermissions(
      userRole,
      9,
      "order_2",
      accountType
    )
    const isPermissionAssign = getSubPermissions(
      userRole,
      9,
      "order_3",
      accountType
    )
    const canViewOrders = isPermissionEnabled(
      userRole,
      9,
      "order_1",
      accountType
    )

    if (!canViewOrders) {
      navigateToAccessibleRoute(navigate, { userRole, accountType })
      return
    }
    setUsersPermissions({
      list: canViewOrders,
      edit:
        isPermissionEnabled(userRole, 9, "order_2", accountType) ||
        isPermissionEdit?.update_orders?.isEnabled,
      assignToRider:
        isTenantOwnerAdmin() ||
        isPermissionAssign?.assign_orders_to_rider?.isEnabled,
      assignToBranch:
        isTenantOwnerAdmin() ||
        isPermissionAssign?.assign_orders_to_branch?.isEnabled,
      export: canExportOrders({
        userRole,
        accountType,
      }),
    })
  }, [userRole, accountType, navigate])

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleSearchChange2 = e => {
    setSearchTerm2(e.target.value)
  }

  const handleAppVersionChange = e => {
    // setAppVersion(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => {
      const updatedFilters = {
        ...prevFilters,
        [filterName]: selectedValue,
      }

      if (filterName === "platform" && selectedValue.length === 0) {
        updatedFilters.appVersion = "" // Reset appVersion if platform is cleared
      }

      if (filterName === "branchId" && activeView === "map") {
        updatedFilters.riderId = ""
        if (canFilterByRider) {
          const branchIds = (selectedValue || []).map(item =>
            String(item.value)
          )
          fetchRiderFilterOptions(branchIds)
        }
      }

      return updatedFilters
    })
  }

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  const commitAppliedMapFilters = useCallback(
    (filters, nextDateRange, nextEstimatedDeliveryDateRange = {}) => {
      setAppliedMapFilters({
        branchId: filters.branchId || [],
        riderId: filters.riderId || "",
        status: filters.status,
        assignmentStatus: filters.assignmentStatus || "",
        estimatedDeliveryDateRange:
          nextEstimatedDeliveryDateRange?.min &&
          nextEstimatedDeliveryDateRange?.max
            ? {
                min: nextEstimatedDeliveryDateRange.min,
                max: nextEstimatedDeliveryDateRange.max,
              }
            : {},
      })
      setAppliedMapDateRange(
        nextDateRange?.min && nextDateRange?.max
          ? { min: nextDateRange.min, max: nextDateRange.max }
          : {}
      )
    },
    []
  )

  const handleFetchOrders = (page = 1, perPageOverride) => {
    const {
      status,
      posStatus,
      sortOrder,
      branchId,
      orderType,
      platform,
      assignmentStatus,
      paymentMethod,
      paymentStatus,
      riderId,
    } = selectedFilters
    const forMap = activeView === "map"
    const nextSearchParams = new URLSearchParams()

    if (!forMap) {
      nextSearchParams.set("search", searchTerm)
      nextSearchParams.set("sortOrder", sortOrder.toString())
      nextSearchParams.set("page", page.toString())
    } else {
      nextSearchParams.set("view", "map")
    }

    nextSearchParams.set("status", status.toString())

    if (canFilterByBranch && branchId?.length > 0) {
      const branchIdString = branchId
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      nextSearchParams.set("branchId", branchIdString)
    }
    if (!forMap && orderType?.length > 0) {
      const orderTypeString = orderType
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      nextSearchParams.set("orderType", orderTypeString)
    }
    if (!forMap && platform?.length > 0) {
      const platformString = platform
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      nextSearchParams.set("platform", platformString)
    }
    if (canFilterByRider && assignmentStatus) {
      nextSearchParams.set("assignmentStatus", assignmentStatus)
    }
    if (canFilterByPaymentMethod && !forMap && paymentMethod?.length > 0) {
      const paymentMethodString = paymentMethod
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      nextSearchParams.set("paymentMethod", paymentMethodString)
    }
    if (
      canFilterByPaymentMethod &&
      !forMap &&
      paymentStatus !== "" &&
      paymentStatus != null
    ) {
      nextSearchParams.set("paymentStatus", String(paymentStatus))
    }
    if (!forMap && posStatus && Number(posStatus) !== 0) {
      nextSearchParams.set("posStatus", String(posStatus))
    }
    if (canFilterByRider && forMap && riderId) {
      nextSearchParams.set("riderId", String(riderId))
    }

    const hasDateRange = dateRange?.min && dateRange?.max
    if (hasDateRange) {
      nextSearchParams.set("minDate", dateRange.min?.toString())
      nextSearchParams.set("maxDate", dateRange.max?.toString())
    }

    const payload = buildOrdersQueryPayload({
      page,
      perPage:
        perPageOverride != null ? Number(perPageOverride) : itemsPerPage,
      forMap,
    })

    dispatch(getAllOrders(payload))
      .then(action => {
        const { statusCode, status: ok } = action?.payload
        if (statusCode === 200 && ok) {
          if (forMap) {
            const appliedEstimatedRange =
              showScheduledDeliveryDate &&
              estimatedDeliveryDateRange.min &&
              estimatedDeliveryDateRange.max
                ? estimatedDeliveryDateRange
                : {}
            commitAppliedMapFilters(
              { branchId, riderId, status, assignmentStatus },
              hasDateRange ? dateRange : {},
              appliedEstimatedRange
            )
          }
          navigate(`${ordersBasePath}?${nextSearchParams.toString()}`)
        }
      })
      .catch(() => {})
  }

  const handleSearch = () => {
    handleFetchOrders(1)
  }

  const onPageChange = page => {
    handleFetchOrders(page)
  }

  // useEffect(() => {
  //   const { platform } = selectedFilters
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters, itemsPerPage])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleDelete = () => {
    setShowDeleteModal(false)
  }

  const handleReset = () => {
    dispatch(setItemsPerPage(commonItemPerPage))

    const resetDateRange = getDefaultDateRange()
    const payload = {
      perPage: commonItemPerPage,
      pageNo: 1,
      orderBy: 2,
      keyword: "",
      status: 0,
      dateRange: resetDateRange,
      estimatedDeliveryDateRange: {},
      serviceProvider,
      brandId,
    }
    if (activeView === "map") {
      dispatch(
        getAllOrders({
          perPage: MAP_ORDERS_PER_PAGE,
          pageNo: 1,
          orderBy: 2,
          keyword: "",
          orderNo: "",
          status: 0,
          branchId: [],
          orderType: ["delivery"],
          assignmentStatus: "",
          serviceProvider,
          brandId,
          dateRange: resetDateRange,
          estimatedDeliveryDateRange: {},
        })
      )
    } else {
      dispatch(getAllOrders(payload))
    }
    setSelectedFilters({
      sortOrder: 2,
      branchId: [],
      status: 0,
      posStatus: 0,
      orderType: [],
      platform: [],
      appVersion: "",
      assignmentStatus: "",
      paymentMethod: [],
      paymentStatus: "",
      riderId: "",
    })
    setSelectedOrderIds([])
    // setAppVersion("")
    setSearchTerm("")
    setSearchTerm2("")
    setDateRange(resetDateRange)
    setEstimatedDeliveryDateRange({})
    setAppliedMapFilters({
      branchId: [],
      riderId: "",
      status: 0,
      assignmentStatus: "",
      estimatedDeliveryDateRange: {},
    })
    setAppliedMapDateRange(resetDateRange)
    ;[listFlatpickrRef, mapFlatpickrRef].forEach(pickerRef => {
      pickerRef.current?.flatpickr?.setDate(
        [resetDateRange.min, resetDateRange.max],
        false
      )
    })
    if (estimatedDeliveryFlatpickrRef.current) {
      estimatedDeliveryFlatpickrRef.current.flatpickr.clear()
    }
    if (mapEstimatedDeliveryFlatpickrRef.current) {
      mapEstimatedDeliveryFlatpickrRef.current.flatpickr.clear()
    }
    if (activeView === "map") {
      navigate(`${ordersBasePath}?view=map`)
    } else {
      navigate(`${ordersBasePath}?page=1`)
    }
  }

  const handleRowClick = id => {
    navigate(`${ordersBasePath}/order-details?id=${id}`)
  }

  const getExportFileName = (suffix = "") => {
    const hasDateRange = dateRange?.min && dateRange?.max
    if (selectedOrderIds.length > 0) {
      return t("Selected F&B Orders{{suffix}}", {
        suffix: suffix ? ` (${suffix.toUpperCase()})` : "",
      })
    }
    if (hasDateRange) {
      return t(`f&b_order_from_${dateRange.min}_to_${dateRange.max}`)
    }
    return t("F&B Orders Till Date")
  }

  const runOrderExport = async exportType => {
    const payload = buildOrderExportPayload({
      exportType,
      selectedFilters,
      searchTerm,
      searchTerm2,
      dateRange,
      estimatedDeliveryDateRange,
      serviceProvider,
      includeServiceProvider: showServiceProviderColumn,
      includePosColumns: showPosStatusColumn,
      includeSalasaColumns: false,
      brandId,
      orderIds: selectedOrderIds,
      resolveListStatus,
    })

    await downloadOrderExport({
      dispatch,
      t,
      payload,
      fileName: getExportFileName(exportType === "xlsx" ? "xlsx" : "csv"),
    })
  }

  const handleExcelDownload = async () => {
    await runOrderExport("xlsx")
  }

  const exportToCSV = async () => {
    await runOrderExport("csv")
  }

  const handleExportSelected = async exportType => {
    if (!selectedOrderIds.length) return
    await runOrderExport(exportType)
  }

  // Convert bookingStatus to options array for react-select
  const statusOptions = Object.entries(activeOrderStatusMap).map(
    ([value, label]) => ({
      value,
      label: t(label),
    })
  )

  const handleStatusChange = async (id, status) => {
    const order = orders?.find(item => item._id === id)
    const nextStatus = Number(status)
    const isDeliveryOrder = order?.orderType === "delivery"
    // Rider app owns Out for Delivery (3) and Delivered (4) for delivery orders
    if (isDeliveryOrder && (nextStatus === 3 || nextStatus === 4)) {
      dispatch(
        showAlert({
          text: t(
            "Out for delivery and delivered statuses are updated by the rider app"
          ),
          type: "error",
        })
      )
      return
    }

    const confirm = await Swal.fire({
      title: t("Are you sure?"),
      text: t("Do you want to update this order status?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("Yes"),
      cancelButtonText: t("Cancel"),
      reverseButtons: true,
    })
    if (!confirm.isConfirmed) {
      return
    }

    setSelectedId(id)
    const payload = {
      id,
      status,
    }
    dispatch(updateOrderStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setSelectedId(null)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleSentOnPOS = () => {
    const payload = {
      orderId: selectedId,
    }
    dispatch(syncOrderWithPos(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setSelectedId(null)
          handleFetchOrders()
          setShowModal(false)
        } else {
          dispatch(
            showAlert({
              text: t(POS_PUSH_FAILED_MESSAGE),
              type: "error",
            })
          )
        }
      })
      .catch(() => {
        dispatch(
          showAlert({
            text: t(POS_PUSH_FAILED_MESSAGE),
            type: "error",
          })
        )
      })
  }

  const perPageOptions = getPerPageOptions(totalCount, itemsPerPage)

  const handlePerPageChange = e => {
    const nextPerPage = Number(e.target.value)
    if (!nextPerPage || nextPerPage === Number(itemsPerPage)) return
    dispatch(setItemsPerPage(nextPerPage))
    handleFetchOrders(1, nextPerPage)
  }

  const handleDownloadOrderPdf = async (orderId, e) => {
    e?.stopPropagation()
    e?.preventDefault()
    if (!orderId || downloadingPdfId) return
    setDownloadingPdfId(String(orderId))
    try {
      await downloadOrderDetailPdf(orderId, {
        includeWalletAmount: hasWalletRelatedPermission(
          userRole,
          accountType,
          accountScope
        ),
      })
    } catch (error) {
      dispatch(
        showAlert({
          text: error?.message || t("Download failed."),
          type: "error",
        })
      )
    } finally {
      setDownloadingPdfId(null)
    }
  }

  const getOrderId = order => String(order?._id || order?.id || "")

  const selectedOrders = orders.filter(o =>
    selectedOrderIds.includes(getOrderId(o))
  )
  const hasSelection = selectedOrderIds.length > 0
  const canAssignToRider =
    usersPermissions.assignToRider &&
    selectedOrders.length > 0 &&
    selectedOrders.every(o => o.orderType === "delivery")

  const toggleOrderSelection = (orderId, e) => {
    e?.stopPropagation()
    const id = String(orderId)
    if (!id) return
    setSelectedOrderIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const toggleSelectAllOnPage = e => {
    e?.stopPropagation()
    const pageIds = orders.map(o => getOrderId(o)).filter(Boolean)
    const allSelected = pageIds.every(id => selectedOrderIds.includes(id))
    if (allSelected) {
      setSelectedOrderIds(prev => prev.filter(id => !pageIds.includes(id)))
    } else {
      setSelectedOrderIds(prev => [...new Set([...prev, ...pageIds])])
    }
  }

  const isAllOnPageSelected =
    orders.length > 0 &&
    orders.every(o => selectedOrderIds.includes(getOrderId(o)))

  const handleAssignSuccess = () => {
    setSelectedOrderIds([])
    handleFetchOrders(currentPage)
  }

  const handleMapAssignSuccess = () => {
    handleFetchOrders(1)
    if (canFilterByBranch) {
      fetchMapBranches()
    }
  }

  const handleViewChange = view => {
    if (view === activeView) return
    setActiveView(view)
    setSelectedOrderIds([])

    const nextParams = new URLSearchParams(location.search)
    if (view === "map") {
      nextParams.set("view", "map")
      nextParams.delete("page")
    } else {
      nextParams.delete("view")
      nextParams.set("page", "1")
    }

    navigate(`${ordersBasePath}?${nextParams.toString()}`, { replace: true })

    const payload = buildOrdersQueryPayload({
      page: 1,
      forMap: view === "map",
    })
    dispatch(getAllOrders(payload)).then(action => {
      if (
        view === "map" &&
        action?.payload?.statusCode === 200 &&
        action?.payload?.status
      ) {
        commitAppliedMapFilters(
          selectedFilters,
          dateRange,
          estimatedDeliveryDateRange
        )
      }
    })
  }

  const handleMapOrderClick = order => {
    setMapSelectedOrder(order)
    setShowMapOrderModal(true)
  }

  const mapFocusBranchIds = useMemo(
    () => appliedMapFilters.branchId?.map(item => String(item.value)) || [],
    [appliedMapFilters.branchId]
  )

  const filteredMapBranches = useMemo(() => {
    if (!mapFocusBranchIds.length) return mapBranches
    return mapBranches.filter(branch =>
      mapFocusBranchIds.includes(String(branch._id))
    )
  }, [mapBranches, mapFocusBranchIds])

  const mapBranchesOnMap = useMemo(
    () => filteredMapBranches.filter(branch => getBranchCoords(branch)),
    [filteredMapBranches]
  )

  const mapOrdersForDisplay = useMemo(
    () => filterOrdersForAppliedMapFilters(orders, appliedMapFilters),
    [orders, appliedMapFilters]
  )

  const mapOrderTotalCount =
    activeView === "map" && typeof totalCount === "number"
      ? totalCount
      : mapOrdersForDisplay.length

  const deliveryStatusLabel = status => {
    const labels = {
      unassigned: t("Not Assigned"),
      assigned: t("Assigned"),
      in_progress: t("In Progress"),
      delivered: t("Delivered"),
      cancelled: t("Cancelled"),
    }
    return labels[status] || status || "-"
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                cardTitle={pageTitle}
                isAddBtn={false}
                isExportData={loading ? false : usersPermissions.export}
                isCustomPage={true}
                perPageOptions={perPageOptions}
                itemsPerPage={itemsPerPage}
                handlePerPageChange={handlePerPageChange}
              />
              <CardBody>
                {showMapTab && (
                  <Nav tabs className="mb-3">
                    <NavItem>
                      <NavLink
                        className={activeView === "list" ? "active" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleViewChange("list")}
                      >
                        {t("List")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeView === "map" ? "active" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleViewChange("map")}
                      >
                        {t("Map")}
                      </NavLink>
                    </NavItem>
                  </Nav>
                )}

                <TabContent activeTab={activeView}>
                  <TabPane tabId="list">
                {/* {!loading && ( */}
                <Filters
                  dateRangeWithTime={true}
                  filters={listFilters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  pageSizes={[0, 1, 2]}
                  itemsPerPage={50}
                  setItemsPerPage={null}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={listFlatpickrRef}
                  searchBy={t("Name, Phone number")}
                  isSecondSearch={true}
                  searchTerm2={searchTerm2}
                  handleSearchChange2={handleSearchChange2}
                  searchBy2={t("Order No.")}
                  colSize={4}
                  isEstimatedDeliveryDateRange={showScheduledDeliveryDate}
                  estimatedDeliveryDateRange={estimatedDeliveryDateRange}
                  setEstimatedDeliveryDateRange={setEstimatedDeliveryDateRange}
                  estimatedDeliveryFlatpickrRef={estimatedDeliveryFlatpickrRef}
                />
                {hasSelection &&
                  (usersPermissions.assignToRider ||
                    usersPermissions.assignToBranch ||
                    usersPermissions.export) && (
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 bg-light rounded">
                      <span className="text-muted me-2">
                        {t("{{count}} selected", {
                          count: selectedOrderIds.length,
                        })}
                      </span>
                      {usersPermissions.export && (
                        <>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleExportSelected("xlsx")}
                          >
                            {t("Export Selected (Excel)")}
                          </Button>
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handleExportSelected("csv")}
                          >
                            {t("Export Selected (CSV)")}
                          </Button>
                        </>
                      )}
                      {usersPermissions.assignToRider && (
                        <Button
                          color="primary"
                          size="sm"
                          disabled={!canAssignToRider}
                          title={
                            !canAssignToRider
                              ? t("Only delivery orders can be assigned to a rider")
                              : ""
                          }
                          onClick={() => setShowAssignRiderModal(true)}
                        >
                          {t("Assign to Rider")}
                        </Button>
                      )}
                      {usersPermissions.assignToBranch && (
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={() => setShowAssignBranchModal(true)}
                        >
                          {t("Assign to Branch")}
                        </Button>
                      )}
                      <Button
                        color="link"
                        size="sm"
                        className="text-danger"
                        onClick={() => setSelectedOrderIds([])}
                      >
                        {t("Clear selection")}
                      </Button>
                    </div>
                  )}
                {/* )} */}
                <OrdersTableScroll>
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : orders.length === 0 ? (
                    <div className="g_center" style={{ minHeight: 200 }}>
                      <p className="text-center my-2 mb-2">
                        {t("No orders found")}
                      </p>
                    </div>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          {(usersPermissions.assignToRider ||
                            usersPermissions.assignToBranch ||
                            usersPermissions.export) && (
                            <th
                              style={{ width: 40 }}
                              onClick={e => e.stopPropagation()}
                              onMouseDown={e => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input cursor-pointer"
                                checked={isAllOnPageSelected}
                                onClick={toggleSelectAllOnPage}
                                aria-label={t("Select all orders on page")}
                              />
                            </th>
                          )}
                          {/* <th style={{ whiteSpace: "nowrap" }}>
                            {t("S. No.")}
                          </th> */}
                          <th>{t("Order No")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Phone Number")}</th>
                          {/* <th>Email</th> */}
                          {/* <th>{t("Platform")}</th> */}
                          <th>{t("Amount")}</th>
                          <th>{t("Order Type")}</th>
                          {showScheduledDeliveryDate ? (
                            <th>{t("Est. Delivery Date")}</th>
                          ) : null}
                          <th>{t("Branch Name")}</th>
                          <th>{t("Rider")}</th>
                          <th>{t("Rider Assignment")}</th>
                          {showServiceProviderColumn && (
                            <th>{t("Service Provider")}</th>
                          )}
                          <th>{t("Date & Time")}</th>
                          <th>{t("Payment Method")}</th>
                          <th>{t("Status")}</th>
                          {showPosStatusColumn && <th>{t("POS Status")}</th>}
                          {usersPermissions.edit && (
                            <th style={{ minWidth: 200 }}>
                              {t("Update Status")}
                            </th>
                          )}
                          <th style={{ width: 56, textAlign: "center" }}>
                            {t("Download")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((item, index) => {
                          const itemPosSource = buildOrderPosSource({
                            order: item,
                            brandConfig,
                            assignedBrands: assignedBrandsList,
                          })
                          const itemPosCode =
                            resolveOrderPosIntegrationCode(itemPosSource)
                          const itemRowPushPos =
                            isPushCapablePosBrand(itemPosSource)
                          const itemTenantConn = getTenantIntegrationConnection(
                            itemPosCode,
                            tenantPosByCode
                          )
                          const itemBranchLinked = isOrderPosBranchLinked(
                            item,
                            itemPosCode
                          )
                          const showManualPush = canShowManualPosPushButton({
                            order: item,
                            posIntegrationCode: itemPosCode,
                            tenantConnected: itemTenantConn.connected,
                            branchLinked: itemBranchLinked,
                            canEdit: usersPermissions.edit,
                          })

                          return (
                          <tr
                            key={item._id}
                            onClick={() => {
                              handleRowClick(item._id)
                            }}
                            className="cursor-pointer"
                          >
                            {(usersPermissions.assignToRider ||
                              usersPermissions.assignToBranch ||
                              usersPermissions.export) && (
                              <td
                                onClick={e => e.stopPropagation()}
                                onMouseDown={e => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input cursor-pointer"
                                  checked={selectedOrderIds.includes(
                                    getOrderId(item)
                                  )}
                                  onClick={e =>
                                    toggleOrderSelection(getOrderId(item), e)
                                  }
                                  aria-label={t("Select order")}
                                />
                              </td>
                            )}
                            {/* <th>
                              {" "}
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </th> */}
                            {/* <th scope="row">
                              <Link
                                className="text-decoration-underline gray_text"
                                to={`/orders/order-details?id=${item._id}`}
                              >
                                {item._id}
                              </Link>
                            </th> */}
                            <td>
                              <OrderNoCopyCell orderNumber={item.invoiceNo} />
                            </td>
                            <td>{item?.name || "-"}</td>
                            <td>
                              {item?.phone ? (
                                <LtrText>{item.phone}</LtrText>
                              ) : (
                                "-"
                              )}
                            </td>{" "}
                            {/* <td>{item.email || "-"}</td> */}
                            {/* <td key={language}>
                              {(() => {
                                const platform = String(
                                  item.platform || ""
                                ).toLowerCase()
                                if (platform === "android") return t("Android")
                                if (platform === "ios") return t("iOS")
                                if (platform === "website") return t("Website")
                                return item.platform ? t(item.platform) : "-"
                              })()}
                              {["android", "ios"].includes(
                                String(item.platform || "").toLowerCase()
                              ) &&
                                item?.appVersion &&
                                ` (${formatAppVersionLabel(item.appVersion)})`}
                            </td> */}
                            <td>
                              {item.paidAmount}{" "}
                              {getCurrencyLabel(
                                item?.splitPayments?.currency
                              )}
                            </td>
                            <td className="text-capitalize">
                              <div className="d-flex flex-column align-items-start gap-1">
                                <span>{t(item.orderType)}</span>
                                {item.orderType === "curbside" &&
                                  item.arrivalAt && (
                                    <>
                                      <Badge
                                        id={`curbside_arrived_${item._id}`}
                                        color="success"
                                        className="badge-soft-success font-size-11"
                                      >
                                        {t("Arrived")}
                                      </Badge>
                                      <UncontrolledTooltip
                                        placement="top"
                                        target={`curbside_arrived_${item._id}`}
                                      >
                                        <div className="text-start">
                                          {(language === "sa"
                                            ? item.curbsideVehicleBrandAr
                                            : item.curbsideVehicleBrand) && (
                                            <div>
                                              {t("Name")}:{" "}
                                              {language === "sa"
                                                ? item.curbsideVehicleBrandAr
                                                : item.curbsideVehicleBrand}
                                            </div>
                                          )}
                                          {(language === "sa"
                                            ? item.curbsideVehicleColorAr
                                            : item.curbsideVehicleColor) && (
                                            <div>
                                              {t("Color")}:{" "}
                                              {language === "sa"
                                                ? item.curbsideVehicleColorAr
                                                : item.curbsideVehicleColor}
                                            </div>
                                          )}
                                          {item.curbsideVehiclePlate && (
                                            <div>
                                              {t("Plate Number")}:{" "}
                                              {item.curbsideVehiclePlate}
                                            </div>
                                          )}
                                          {item.parkingNumber && (
                                            <div>
                                              {t("Parking Number")}:{" "}
                                              {item.parkingNumber}
                                            </div>
                                          )}
                                        </div>
                                      </UncontrolledTooltip>
                                    </>
                                  )}
                              </div>
                            </td>
                            {showScheduledDeliveryDate ? (
                              <td>
                                {item.orderType === "delivery" &&
                                item.estimatedDeliveryDate
                                  ? formatDisplayDateTime(
                                      item.estimatedDeliveryDate
                                    )
                                  : "-"}
                              </td>
                            ) : null}
                            <td className="text-capitalize">
                              {language === "sa"
                                ? item?.branchNameAr
                                : item?.branchName}
                            </td>
                            <td>
                              {item.orderType === "delivery"
                                ? item.deliveryPartnerName || "-"
                                : "-"}
                            </td>
                            <td>
                              {item.orderType === "delivery"
                                ? deliveryStatusLabel(item.deliveryStatus)
                                : "-"}
                            </td>
                            {showServiceProviderColumn && (
                              <td className="text-capitalize">
                                {t(item.serviceProvider)}
                              </td>
                            )}
                            <td>{formatDisplayDateTime(item.createdAt)}</td>
                            <td>
                              <i
                                className={`${
                                  item.paymentMethod === "CARD"
                                    ? "fab fa-cc-mastercard me-1"
                                    : "" || item.paymentMethod === "APPLE"
                                    ? "fab bx bxl-apple me-1"
                                    : ""
                                }`}
                              ></i>
                              {t(item.paymentMethod)}
                            </td>
                            <td>
                              {/* System status — orderType-aware labels for 3/4 */}
                              {(() => {
                                const display = getSystemStatusDisplay(
                                  itemPosSource,
                                  t
                                )
                                return (
                                  <span
                                    style={{
                                      color: display.color,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {display.text}
                                  </span>
                                )
                              })()}
                            </td>
                            {showPosStatusColumn && itemRowPushPos && (
                              <td>
                                <span
                                  style={{
                                    color: getPosStatusColor(itemPosSource),
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {getPosStatusLabel(itemPosSource, t)}
                                </span>
                              </td>
                            )}
                            {showPosStatusColumn && !itemRowPushPos && (
                              <td>-</td>
                            )}
                            {/* <td>
                              {usersPermissions.list && (
                                <Button
                                  type="button"
                                  color="primary"
                                  className="btn-sm btn-rounded"
                                  onClick={() => {
                                    navigate(
                                      `/orders/order-details?id=${item._id}`
                                    )
                                  }}
                                >
                                  View Details
                                </Button>
                              )}
                            </td> */}
                            {usersPermissions.edit && (
                              <th onClick={e => e.stopPropagation()}>
                                {showManualPush ? (
                                  <Button
                                    color="primary"
                                    title={t("Sync Order With POS")}
                                    onClick={() => {
                                      setShowModal(true)
                                      setSelectedId(item._id)
                                    }}
                                  >
                                    {t("Send on POS")}
                                  </Button>
                                ) : (
                                  <Select
                                    value={{
                                      value: resolveSystemOrderStatusValue(
                                        item
                                      ).toString(),
                                      label: getSystemOrderStatusLabel(
                                        resolveSystemOrderStatusValue(item),
                                        item.orderType,
                                        t
                                      ),
                                    }}
                                    isDisabled={
                                      selectedId === item._id && isLoading
                                    }
                                    onChange={(selectedOption, event) => {
                                      if (event.nativeEvent) {
                                        event.nativeEvent.stopPropagation()
                                      }
                                      handleStatusChange(
                                        item._id,
                                        selectedOption.value
                                      )
                                    }}
                                    options={statusOptions
                                      .filter(option => {
                                        const currentStatus =
                                          resolveSystemOrderStatusValue(item)
                                        const optionValue = parseInt(
                                          option.value
                                        )
                                        const isDelivery =
                                          item.orderType === "delivery"

                                        switch (currentStatus) {
                                          case 1:
                                            return (
                                              optionValue === 2 ||
                                              optionValue === 5
                                            )
                                          case 2:
                                            // Delivery: Out for Delivery (3) is rider-managed
                                            if (isDelivery) {
                                              return optionValue === 5
                                            }
                                            return (
                                              optionValue === 3 ||
                                              optionValue === 5
                                            )
                                          case 3:
                                            // Delivery: Delivered (4) is rider-managed
                                            if (isDelivery) {
                                              return optionValue === 5
                                            }
                                            return (
                                              optionValue === 4 ||
                                              optionValue === 5
                                            )
                                          case 4:
                                            return false
                                          case 5:
                                            return false
                                          default:
                                            return false
                                        }
                                      })
                                      .map(option => ({
                                        ...option,
                                        label: getSystemOrderStatusLabel(
                                          option.value,
                                          item.orderType,
                                          t
                                        ),
                                        isDisabled:
                                          parseInt(option.value) ===
                                          resolveSystemOrderStatusValue(item),
                                      }))}
                                    placeholder={t("Update Status")}
                                    noOptionsMessage={() => t("No options")}
                                    {...ORDER_STATUS_SELECT_MENU_PROPS}
                                    styles={orderStatusSelectStyles}
                                  />
                                )}
                              </th>
                            )}
                            <td
                              style={{ textAlign: "center" }}
                              onClick={e => e.stopPropagation()}
                              onMouseDown={e => e.stopPropagation()}
                            >
                              <i
                                id={`download_order_pdf_${item._id}`}
                                className={`mdi ${
                                  downloadingPdfId === String(item._id)
                                    ? "mdi-loading mdi-spin"
                                    : "mdi-download"
                                } font-size-18 text-primary cursor-pointer`}
                                onClick={e =>
                                  handleDownloadOrderPdf(item._id, e)
                                }
                              ></i>
                              <UncontrolledTooltip
                                placement="top"
                                target={`download_order_pdf_${item._id}`}
                              >
                                {t("Download Order Details PDF")}
                              </UncontrolledTooltip>
                            </td>
                          </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  )}
                </OrdersTableScroll>
                {!loading && orders.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={orders}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
                  </TabPane>

                  {showMapTab && (
                    <TabPane tabId="map">
                      <Filters
                        dateRangeWithTime={true}
                        filters={mapFilters}
                        hideSearch={true}
                        handleFilterChange={handleFilterChange}
                        handleSearch={handleSearch}
                        onReset={handleReset}
                        selectedFilters={selectedFilters}
                        isRangeDate={true}
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        flatpickrRef={mapFlatpickrRef}
                        colSize={4}
                        isEstimatedDeliveryDateRange={showScheduledDeliveryDate}
                        estimatedDeliveryDateRange={estimatedDeliveryDateRange}
                        setEstimatedDeliveryDateRange={
                          setEstimatedDeliveryDateRange
                        }
                        estimatedDeliveryFlatpickrRef={
                          mapEstimatedDeliveryFlatpickrRef
                        }
                      />
                      <OrdersDeliveryMap
                        branches={filteredMapBranches}
                        orders={mapOrdersForDisplay}
                        filteredOrderTotal={mapOrderTotalCount}
                        maxOrdersOnMap={MAP_ORDERS_PER_PAGE}
                        branchCount={mapBranchesOnMap.length}
                        loading={loading || mapBranchesLoading}
                        onOpenOrderModal={handleMapOrderClick}
                        language={language}
                        focusBranchIds={mapFocusBranchIds}
                        isActive={activeView === "map"}
                        showEstimatedDeliveryDate={showScheduledDeliveryDate}
                      />
                    </TabPane>
                  )}
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <Modal size="md" isOpen={showModal} centered={true}>
        <div className="modal-content">
          <ModalBody className="px-4 py-5 text-center">
            <button
              type="button"
              onClick={() => {
                setShowModal(false)
                setSelectedId(null)
              }}
              className="btn-close position-absolute end-0 top-0 m-3"
            ></button>
            <div className="avatar-sm mb-4 mx-auto">
              <div className="avatar-title bg-primary text-primary bg-opacity-10 font-size-20 rounded-3">
                <i className="bx bx-sync"></i>
              </div>
            </div>
            <p className="text-muted font-size-16 mb-4">
              {t("Are you sure you want to Sync order with POS")}
            </p>

            <div className="hstack gap-2 justify-content-center mb-0">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowModal(false)
                  setSelectedId(null)
                }}
              >
                {t("No")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSentOnPOS}
                disabled={isLoading}
                title={t("Sync Order With POS")}
              >
                {t("Yes")}
              </button>
            </div>
          </ModalBody>
        </div>
      </Modal>
      <AssignToRiderModal
        isOpen={showAssignRiderModal}
        toggle={() => setShowAssignRiderModal(false)}
        selectedOrderIds={selectedOrderIds}
        selectedOrders={selectedOrders}
        branchsList={branchsList}
        onSuccess={handleAssignSuccess}
      />
      <AssignToBranchModal
        isOpen={showAssignBranchModal}
        toggle={() => setShowAssignBranchModal(false)}
        selectedOrderIds={selectedOrderIds}
        selectedOrders={selectedOrders}
        branchsList={branchsList}
        onSuccess={handleAssignSuccess}
      />
      <OrderMapDetailModal
        isOpen={showMapOrderModal}
        toggle={() => {
          setShowMapOrderModal(false)
          setMapSelectedOrder(null)
        }}
        order={mapSelectedOrder}
        branchsList={branchsList}
        canAssign={usersPermissions.assignToRider}
        ordersBasePath={ordersBasePath}
        onSuccess={handleMapAssignSuccess}
        showEstimatedDeliveryDate={showScheduledDeliveryDate}
      />
    </div>
  )
}

export default Orders
